import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Calendar, User, Tag, BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GH", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function readingTime(body = "") {
  const words = body.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "F";
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setPost(null); setNotFound(false);
    axios.get(`${API}/blog/${slug}`)
      .then((r) => { if (!cancelled) setPost(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    axios.get(`${API}/blog`)
      .then((r) => { if (!cancelled) setRelated(r.data.posts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="blog-not-found" className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-heading text-4xl">Post not found.</h1>
        <Link to="/blog" className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to the Journal
        </Link>
      </div>
    );
  }
  if (!post) {
    return <div className="max-w-3xl mx-auto px-4 py-40 text-center text-faytex-muted">Loading…</div>;
  }

  const others = related.filter((p) => p.slug !== post.slug).slice(0, 3);
  const paragraphs = (post.body || "").split(/\n\n+/).filter(Boolean);

  return (
    <article data-testid="blog-post-page" className="bg-white">
      {/* HERO */}
      <section className="relative isolate bg-faytex-pinkLight/60">
        <div className="grain absolute inset-0 -z-0" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-12 md:pb-16">
          <Link to="/blog" data-testid="post-back" className="inline-flex items-center gap-2 text-sm text-faytex-muted hover:text-faytex-pink">
            <ArrowLeft className="w-4 h-4" /> Back to the Journal
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-faytex-pink/10 text-faytex-pinkHover px-3 py-1 text-[11px] uppercase tracking-widest font-semibold">
              <BookOpen className="w-3 h-3" /> Faytex Journal
            </span>
            {post.tags?.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-white text-faytex-pinkHover ring-1 ring-faytex-pink/20 px-3 py-1 text-[11px] uppercase tracking-widest font-semibold">
                <Tag className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>
          <h1 data-testid="post-title" className="font-heading text-4xl md:text-6xl mt-6 tracking-tighter leading-[1.05]">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-5 text-lg md:text-xl text-faytex-muted leading-relaxed">{post.excerpt}</p>
          )}
          <div className="mt-8 flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-faytex-pink text-white flex items-center justify-center font-heading text-sm" aria-hidden="true">
              {initials(post.author)}
            </span>
            <div className="text-sm">
              <p className="font-semibold text-faytex-ink">{post.author}</p>
              <p className="text-xs text-faytex-muted flex items-center gap-2">
                <Calendar className="w-3 h-3" /> {fmtDate(post.published_at)}
                <span aria-hidden="true">·</span>
                <span>{readingTime(post.body)} min read</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COVER */}
      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 md:-mt-10 relative z-10">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-hover">
            <img src={resolveImg(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      {/* BODY */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div data-testid="post-body" className="text-faytex-ink text-lg leading-relaxed space-y-6">
          {paragraphs.map((p, i) => (
            <p
              key={i}
              className={`whitespace-pre-line ${
                i === 0
                  ? "first-letter:font-heading first-letter:text-6xl first-letter:font-bold first-letter:text-faytex-pink first-letter:mr-2 first-letter:float-left first-letter:leading-none first-letter:mt-1"
                  : ""
              }`}
            >
              {p}
            </p>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-faytex-pink hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> All Journal stories
          </Link>
          <Link
            to="/fact-or-faytex"
            className="inline-flex items-center gap-2 rounded-full bg-faytex-ink hover:bg-faytex-pink text-white px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            Have a question? Ask our AI <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* RELATED */}
      {others.length > 0 && (
        <section className="bg-faytex-pinkLight/40 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Keep reading</p>
                <h2 className="font-heading text-3xl md:text-4xl mt-2 tracking-tight">More from the Journal</h2>
              </div>
              <Link to="/blog" className="text-sm font-semibold text-faytex-pink hover:underline">All stories →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-soft hover:shadow-hover transition-all"
                >
                  <div className="aspect-[16/10] bg-faytex-pinkLight/40 overflow-hidden">
                    {p.cover_image && (
                      <img
                        src={resolveImg(p.cover_image)}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-[11px] uppercase tracking-widest text-faytex-muted">{fmtDate(p.published_at)}</p>
                    <h3 className="font-heading text-xl mt-2 group-hover:text-faytex-pink transition-colors leading-tight">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
